import type {
  WeightProfile,
  DimensionScores,
  DimensionKey,
  LandmarkTier,
  DetractorConfig,
  DiagnosticBlockingFactor,
  DiagnosticSuggestion,
  DiagnosticCardResult,
} from '@frame/scoring-types';
import {
  LANDMARK_COEFFICIENTS,
  DEFAULT_DETRACTOR_CONFIG,
} from '@frame/scoring-types';

export function normalizeWeights(profile: WeightProfile): WeightProfile {
  const total =
    profile.portraitWeight + profile.lightingWeight +
    profile.compositionWeight + profile.sceneWeight;
  if (total <= 0) return profile;
  return {
    ...profile,
    portraitWeight: profile.portraitWeight / total,
    lightingWeight: profile.lightingWeight / total,
    compositionWeight: profile.compositionWeight / total,
    sceneWeight: profile.sceneWeight / total,
  };
}

/** Get effective weights, redistributing portrait weight if no face detected. */
export function getEffectiveWeights(
  profile: WeightProfile,
  isLandscape: boolean,
): Record<DimensionKey, number> {
  const norm = normalizeWeights(profile);
  const weights: Record<DimensionKey, number> = {
    portraitComposition: norm.portraitWeight,
    lighting: norm.lightingWeight,
    composition: norm.compositionWeight,
    sceneInterest: norm.sceneWeight,
  };

  if (isLandscape && weights.portraitComposition > 0) {
    const fw = weights.portraitComposition;
    weights.portraitComposition = 0;
    weights.sceneInterest += fw * 0.50;
    weights.lighting += fw * 0.33;
    weights.composition += fw * 0.17;

    // Renormalize
    const t = weights.lighting + weights.composition + weights.sceneInterest;
    if (t > 0) {
      weights.lighting /= t; weights.composition /= t; weights.sceneInterest /= t;
    }
  }

  return weights;
}

// Patent: Claim 3 — Landmark spike coefficient system.
// Applies tier-based multiplicative coefficient to scene interest score.
// Tiers: Iconic (1.5×), Notable (1.25×), Interesting (1.1×), Ordinary (1.0×).
/** Apply landmark spike to scene interest score. */
export function applyLandmarkSpike(
  baseScene: number,
  tier: LandmarkTier,
  spikeStrength: number,
): number {
  const coefficient = LANDMARK_COEFFICIENTS[tier];
  const spiked = baseScene + (coefficient - 1.0) * spikeStrength * baseScene;
  return Math.min(spiked, 10.0);
}

// Patent: Claim 1 — Transparent composite scoring system.
// Computes weighted sum of per-dimension scores with mode-specific weight profiles.
// Patent: Claim 4 — Multiplicative detractor penalty system.
// composite_adjusted = composite × (1 - penalty_weight × detractor_severity)
/** Compute the final composite score from dimension scores + profile. */
export function computeComposite(
  dimensions: DimensionScores,
  profile: WeightProfile,
  detractorPenalty: number = 0,
): { rawComposite: number; finalComposite: number } {
  const isLandscape = dimensions.portraitComposition === null;
  const weights = getEffectiveWeights(profile, isLandscape);

  let weightedSum = 0;
  let totalWeight = 0;

  if (dimensions.portraitComposition !== null && weights.portraitComposition > 0) {
    weightedSum += dimensions.portraitComposition * weights.portraitComposition;
    totalWeight += weights.portraitComposition;
  }

  weightedSum += dimensions.lighting * weights.lighting;
  totalWeight += weights.lighting;

  weightedSum += dimensions.composition * weights.composition;
  totalWeight += weights.composition;

  const sceneScore = dimensions.sceneInterestSpiked;
  weightedSum += sceneScore * weights.sceneInterest;
  totalWeight += weights.sceneInterest;

  const rawComposite = totalWeight > 0 ? weightedSum / totalWeight : 0;

  const finalComposite = profile.detractorPenaltyEnabled
    ? Math.max(0, Math.min(10, rawComposite * (1 - detractorPenalty * profile.detractorPenaltyStrength)))
    : rawComposite;

  return { rawComposite, finalComposite: Math.round(finalComposite * 10) / 10 };
}

// Patent: Claim 4(c) — Crowd-based detractor penalty.
// Applies progressive penalty based on detected face count.
/** Compute crowd penalty from face count. */
export function computeCrowdPenalty(
  faceCount: number,
  config: DetractorConfig = DEFAULT_DETRACTOR_CONFIG,
): number {
  for (let i = config.crowdThresholds.length - 1; i >= 0; i--) {
    if (faceCount >= config.crowdThresholds[i].faces) {
      return config.crowdThresholds[i].penalty;
    }
  }
  return 0;
}

// ─── PATENT CLAIM: Weakest-Dimension-First Edit Prioritization ──
// No prior art for this specific logic (see prior art report).
// Walmart US11,688,049 does threshold-based correction per dimension.
// NIMA uses score-as-loss for general enhancement.
// FRAME's innovation: automated identification of weakest dimension,
// editing targeted to that dimension with intensity PROPORTIONAL to
// the score deficit from a quality threshold.

export interface EditPriority {
  dimension: DimensionKey;
  score: number;
  deficit: number;         // threshold - score (higher = more editing needed)
  editIntensity: number;   // 0-1, proportional to deficit
  editOperations: string[];
  priorityRank: number;    // 1 = weakest, edit first
}

export interface EditPlan {
  priorities: EditPriority[];
  totalDeficit: number;
  weakestDimension: DimensionKey;
  strongestDimension: DimensionKey;
  summary: string;
}

const EDIT_OPERATIONS: Record<DimensionKey, string[]> = {
  portraitComposition: ['face_relight', 'skin_smooth', 'eye_brighten', 'background_blur'],
  lighting: ['exposure_correct', 'shadow_recovery', 'highlight_recovery', 'white_balance', 'contrast_enhance'],
  composition: ['auto_crop', 'horizon_level', 'vignette_add', 'distraction_remove'],
  sceneInterest: ['vibrance_boost', 'clarity_enhance', 'color_grade', 'sky_enhance', 'detail_sharpen'],
};

/**
 * Generate an edit plan that targets the weakest dimension first
 * with intensity proportional to the score deficit.
 * 
 * This is the core of FRAME's score-card-driven editing pipeline.
 * The score card IS the edit recipe.
 * 
 * @param dimensions - The per-dimension scores from scoring
 * @param qualityThreshold - Target quality level (default 7.5)
 * @returns EditPlan with prioritized edit operations
 */
export function generateEditPlan(
  dimensions: DimensionScores,
  qualityThreshold: number = 7.5,
): EditPlan {
  // Build deficit map for all active dimensions
  const entries: Array<{ key: DimensionKey; score: number }> = [];

  if (dimensions.portraitComposition !== null) {
    entries.push({ key: 'portraitComposition', score: dimensions.portraitComposition });
  }
  entries.push({ key: 'lighting', score: dimensions.lighting });
  entries.push({ key: 'composition', score: dimensions.composition });
  entries.push({ key: 'sceneInterest', score: dimensions.sceneInterestSpiked });

  // Sort by score ascending — weakest first
  entries.sort((a, b) => a.score - b.score);

  const totalDeficit = entries.reduce(
    (sum, e) => sum + Math.max(0, qualityThreshold - e.score), 0
  );

  const priorities: EditPriority[] = entries.map((entry, index) => {
    const deficit = Math.max(0, qualityThreshold - entry.score);
    // Intensity is proportional to deficit, normalized to 0-1
    // Weakest dimension gets highest intensity
    const editIntensity = qualityThreshold > 0
      ? Math.min(1.0, deficit / qualityThreshold)
      : 0;

    return {
      dimension: entry.key,
      score: entry.score,
      deficit: Math.round(deficit * 100) / 100,
      editIntensity: Math.round(editIntensity * 100) / 100,
      editOperations: deficit > 0 ? EDIT_OPERATIONS[entry.key] : [],
      priorityRank: index + 1,
    };
  });

  const weakest = entries[0];
  const strongest = entries[entries.length - 1];

  const deficitDims = priorities.filter(p => p.deficit > 0);
  const summary = deficitDims.length === 0
    ? 'All dimensions meet quality threshold — minimal editing needed.'
    : `${deficitDims.length} dimension${deficitDims.length > 1 ? 's' : ''} below threshold. ` +
      `Targeting ${weakest.key} first (score ${weakest.score.toFixed(1)}, ` +
      `deficit ${priorities[0].deficit.toFixed(1)}, ` +
      `intensity ${(priorities[0].editIntensity * 100).toFixed(0)}%).`;

  return {
    priorities,
    totalDeficit: Math.round(totalDeficit * 100) / 100,
    weakestDimension: weakest.key,
    strongestDimension: strongest.key,
    summary,
  };
}

// Patent: Claim 5(d) — Tier-based edit intensity scaling.
// Higher subscription tiers unlock stronger editing (free=0.3, silver=0.6, gold=1.0).
/**
 * Apply edit intensity scaling based on user tier.
 * Higher tiers unlock stronger editing.
 */
export function scaleEditIntensity(
  plan: EditPlan,
  maxIntensity: number, // 0.3 for lowkey, 0.6 for balanced, 1.0 for highkey
): EditPlan {
  return {
    ...plan,
    priorities: plan.priorities.map(p => ({
      ...p,
      editIntensity: Math.min(p.editIntensity, maxIntensity),
    })),
  };
}

// ─── Diagnostic Card Generation ─────────────────────────────
// Patent: Claim 5 — Score-card-driven editing pipeline.
// The diagnostic card IS the edit recipe: score → diagnose → specific suggestion → estimated new score.
// This unified chain has no identified prior art.
export function generateDiagnosticCard(
  photo: { dimensions: DimensionScores; compositeScore: number; detractorPenalty: number },
  qualityThreshold: number = 7.5,
): DiagnosticCardResult {
  const blockingFactors: DiagnosticBlockingFactor[] = [];
  const suggestions: DiagnosticSuggestion[] = [];

  const dimEntries: Array<{ key: DimensionKey; score: number | null }> = [
    { key: 'portraitComposition', score: photo.dimensions.portraitComposition },
    { key: 'lighting', score: photo.dimensions.lighting },
    { key: 'composition', score: photo.dimensions.composition },
    { key: 'sceneInterest', score: photo.dimensions.sceneInterestSpiked },
  ];

  const SUGGESTION_MAP: Record<DimensionKey, string[]> = {
    portraitComposition: [
      'Adjust face-to-frame ratio with tighter crop',
      'Soften background with portrait mode blur +40%',
      'Brighten eye area: lift shadows +15 on face region',
    ],
    lighting: [
      'Reduce highlights -20, lift shadows +15',
      'Warm white balance +200K for golden tone',
      'Increase contrast +10 for more dynamic range',
    ],
    composition: [
      'Crop to rule-of-thirds alignment on subject',
      'Level horizon -2 degrees',
      'Remove distracting elements from edges',
    ],
    sceneInterest: [
      'Boost vibrance +15 for richer colors',
      'Apply clarity enhancement +20 for detail pop',
      'Add subtle vignette to draw focus to center',
    ],
  };

  let totalImpact = 0;

  for (const { key, score } of dimEntries) {
    if (score === null) continue;
    const deficit = qualityThreshold - score;
    if (deficit > 0) {
      const impactPoints = Math.round(deficit * 10);
      totalImpact += impactPoints;

      const descriptions: Record<DimensionKey, string> = {
        portraitComposition: 'Portrait composition could be stronger',
        lighting: score < 4 ? 'Harsh shadows or flat lighting detected' : 'Lighting is acceptable but not optimal',
        composition: score < 4 ? 'Cluttered or unbalanced framing' : 'Framing could be tighter',
        sceneInterest: score < 4 ? 'Low visual interest in scene' : 'Scene lacks a strong focal point',
      };

      blockingFactors.push({
        dimension: key,
        impactPoints,
        description: descriptions[key],
      });

      const specificSuggestions = SUGGESTION_MAP[key];
      for (const action of specificSuggestions) {
        suggestions.push({
          action,
          specificity: 'specific',
          estimatedScoreDelta: Math.round(deficit * 3) / 10,
        });
      }
    }
  }

  // Add detractor-specific suggestions
  if (photo.detractorPenalty > 0.1) {
    blockingFactors.push({
      dimension: 'composition',
      impactPoints: Math.round(photo.detractorPenalty * 10),
      description: 'Background clutter or crowd detected',
    });
    suggestions.push({
      action: 'Apply background blur or use AI background removal',
      specificity: 'specific',
      estimatedScoreDelta: photo.detractorPenalty * 0.6,
    });
  }

  const estimatedDelta = Math.min(totalImpact * 0.6 / 10, 2.0);
  const estimatedNewScore = Math.min(10, photo.compositeScore + estimatedDelta);

  const dimensionScores: Record<DimensionKey, number | null> = {
    portraitComposition: photo.dimensions.portraitComposition,
    lighting: photo.dimensions.lighting,
    composition: photo.dimensions.composition,
    sceneInterest: photo.dimensions.sceneInterestSpiked,
  };

  return {
    overallScore: photo.compositeScore,
    dimensionScores,
    blockingFactors,
    suggestions,
    estimatedNewScore: Math.round(estimatedNewScore * 10) / 10,
    summary: blockingFactors.length === 0
      ? 'Great photo — all dimensions meet quality threshold.'
      : `${blockingFactors.length} area${blockingFactors.length > 1 ? 's' : ''} holding this photo back. Top priority: ${blockingFactors[0].description}.`,
  };
}
